import { CheckIcon, XIcon } from 'lucide-react'
import { Button, SubtleLoader } from '../../../ui'

export interface PendingAssetUpload {
	tempId: string
	fileName: string
	previewUrl: string
	uploadedAt: number
	status: 'uploading' | 'success' | 'error'
	error?: string
	// 0..1, set once the PUT begins emitting progress events. Undefined while we're still
	// signing (no bytes flowing yet) so the row falls back to the indeterminate loader.
	progress?: number
}

export function AssetUploadOverlay({
	uploads,
	onSendToBackground,
	onDone,
	isFinishing = false,
}: {
	uploads: PendingAssetUpload[]
	onSendToBackground: () => void
	onDone: () => void
	// True while the parent batch is still doing async work (mutations or the
	// cache refetch race). Keeps Done disabled across the gap between "all rows
	// settled" and pendingPostCompletionRef being assigned — otherwise an
	// early-clicked Done loses the scroll-to + auto-select.
	isFinishing?: boolean
}) {
	const total = uploads.length
	const doneCount = uploads.filter((u) => u.status !== 'uploading').length
	const isAnyUploading = uploads.some((u) => u.status === 'uploading')
	const errorCount = uploads.filter((u) => u.status === 'error').length

	const headerSubtitle = isAnyUploading
		? `${doneCount} of ${total} ${total === 1 ? 'file' : 'files'}`
		: errorCount > 0
		? `${errorCount} of ${total} failed`
		: 'All uploaded'

	return (
		<div
			className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40"
			role="dialog"
			aria-modal="true"
			aria-label="Uploading assets"
		>
			<div className="bg-white rounded-md shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]">
				<div className="px-6 pt-5 pb-3 border-b border-grey-100 flex items-start justify-between gap-4">
					<div>
						<div className="text-base font-medium">Uploading assets</div>
						<div className="text-sm text-grey-500 mt-0.5">{headerSubtitle}</div>
					</div>
					{isAnyUploading
						? (
							<button
								type="button"
								onClick={onSendToBackground}
								aria-label="Send to background"
								className="text-grey-400 hover:text-grey-700 hover:bg-grey-100 size-7 rounded inline-flex items-center justify-center transition-colors cursor-pointer appearance-none border-none bg-transparent shrink-0"
							>
								<XIcon className="size-4" />
							</button>
						)
						: null}
				</div>
				<div className="px-3 py-3 overflow-y-auto flex flex-col gap-0.5">
					{uploads.map((upload) => <AssetUploadRow key={upload.tempId} upload={upload} />)}
				</div>
				<div className="px-6 py-3 border-t border-grey-100 flex justify-end">
					<Button
						size="short"
						type="committal"
						onClick={onDone}
						isLoading={isAnyUploading || isFinishing}
					>
						Done
					</Button>
				</div>
			</div>
		</div>
	)
}

function AssetUploadRow({ upload }: { upload: PendingAssetUpload }) {
	return (
		<div className="flex items-center gap-3 px-3 py-2 rounded">
			<div
				style={{
					backgroundImage: `url("${upload.previewUrl}")`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
				className="size-10 rounded shrink-0 bg-grey-100 ring-1 ring-grey-200"
			/>
			<div className="min-w-0 flex-1">
				<div className="text-sm truncate">{upload.fileName}</div>
				{upload.status === 'error' && upload.error
					? <div className="text-xs text-red-600 truncate">{upload.error}</div>
					: null}
			</div>
			<div className="shrink-0 min-w-10 flex items-center justify-end">
				{upload.status === 'uploading' && upload.progress !== undefined
					? (
						<span className="text-xs text-grey-500 tabular-nums">
							{Math.round(upload.progress * 100)}%
						</span>
					)
					: null}
				{upload.status === 'uploading' && upload.progress === undefined
					? <SubtleLoader width={16} />
					: null}
				{upload.status === 'success' ? <CheckIcon className="size-4 text-green-600" /> : null}
				{upload.status === 'error' ? <XIcon className="size-4 text-red-600" /> : null}
			</div>
		</div>
	)
}
